import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { capitalize } from "lodash";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import CuoConfirmDialog from "@/app/admin/_common/CuoConfirmDialog";
import { formatUtcDate } from "@/app/_utils/dayjs";
import { cn } from "@/app/_utils/cn";
import { MoreHorizontal, Pencil, Trash2, Link2, Copy } from "lucide-react";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";
import type { Link } from "../linksPageModel";

function signupCount(link: Link) {
  return (link.signups?.new ?? 0) + (link.signups?.repeat ?? 0);
}

export default function LinkList({
  links,
  onDelete,
  onCopy,
}: {
  links: Link[];
  onDelete: (link: Link) => void;
  onCopy: (link: Link) => void;
}) {
  const [pendingDelete, setPendingDelete] = useState<Link | null>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const actionsMenu = (link: Link) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ml-auto flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
        >
          <MoreHorizontal className="size-4 shrink-0" size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onCopy(link)}>
          <Copy className="mr-2 size-4" />
          Copy link
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/admin/marketing/links/${link.id}`)}>
          <Pencil className="mr-2 size-4" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-red-600 focus:bg-red-50 focus:text-red-700"
          onClick={() => setPendingDelete(link)}
        >
          <Trash2 className="mr-2 size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const confirmDialog = (
    <CuoConfirmDialog
      open={pendingDelete !== null}
      onClose={() => setPendingDelete(null)}
      title="Delete link"
      message="This action cannot be undone. Are you sure you want to delete this link?"
      onConfirm={async () => {
        if (pendingDelete) await onDelete(pendingDelete);
      }}
      icon={<Trash2 />}
    />
  );

  if (isMobile) {
    return (
      <div className="flex flex-col bg-card">
        {links.length === 0 ? (
          <div className="flex min-h-[400px] flex-col">
            <CuoEmpty icon={Link2} text="No links" />
          </div>
        ) : (
          <div className="flex flex-col">
            {links.map((link) => (
              <div
                key={link.id}
                className="flex cursor-pointer items-center gap-2.5 border-b border-border px-4 py-2.5 last:border-0"
                onClick={() => navigate(`/admin/marketing/links/${link.id}`)}
              >
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="truncate text-[14px] font-medium text-foreground">
                    {link.name}
                  </div>
                  <div className="truncate text-[12.5px] text-muted-foreground">
                    {link.redirect}
                  </div>
                </div>
                <span className="flex-none text-[12.5px] text-muted-foreground">
                  {signupCount(link)} {signupCount(link) === 1 ? "signup" : "signups"}
                </span>
                <div onClick={(e) => e.stopPropagation()}>{actionsMenu(link)}</div>
              </div>
            ))}
          </div>
        )}

        {confirmDialog}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className={cn("min-w-[900px] table-fixed", links.length === 0 && "h-full")}
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 w-[22%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Name
            </TableHead>
            <TableHead className="h-8 w-[26%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Redirect
            </TableHead>
            <TableHead className="h-8 w-16 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Visits
            </TableHead>
            <TableHead className="h-8 w-20 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Signups
            </TableHead>
            <TableHead className="h-8 w-[12%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Source
            </TableHead>
            <TableHead className="h-8 w-[12%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Medium
            </TableHead>
            <TableHead className="h-8 w-[14%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Created
            </TableHead>
            <TableHead className="h-8 w-12 pr-6 text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {links.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={8} className="h-full p-0">
                <CuoEmpty icon={Link2} text="No links" />
              </TableCell>
            </TableRow>
          ) : (
            links.map((link) => (
              <TableRow key={link.id}>
                <TableCell className="py-1.5">
                  <span
                    className="cursor-pointer truncate text-[13px] text-foreground hover:underline"
                    onClick={() => navigate(`/admin/marketing/links/${link.id}`)}
                  >
                    {link.name}
                  </span>
                </TableCell>
                <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                  <a
                    href={link.redirect}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {link.redirect}
                  </a>
                </TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-right text-[12.5px] text-muted-foreground">
                  {link.visits > 0 ? link.visits : "-"}
                </TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-right text-[12.5px] text-muted-foreground">
                  {signupCount(link) || "-"}
                </TableCell>
                <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                  {capitalize(link.source ?? "") || "-"}
                </TableCell>
                <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                  {capitalize(link.medium ?? "") || "-"}
                </TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-[12.5px] text-muted-foreground">
                  {formatUtcDate(link.created_at)}
                </TableCell>
                <TableCell className="py-1.5 pr-6 text-right">{actionsMenu(link)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {confirmDialog}
    </div>
  );
}
